import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { PageType } from 'src/app/analytics/newrelic/enums/PageType';
import { NewrelicService } from 'src/app/analytics/newrelic/service/newrelic.service';
import { SubscriptionHandlerService } from '../subscription-handler.service';
import { TabService } from '../tabs/tab.service';
import { DataService } from '../../../data.service';

@Injectable({
  providedIn: 'root',
})
export class ScreenService {
  searchItemPosition = 'NA';
  private previouscreen: string;
  private currentscreen: string;
  private searchLink: string;
  private history: string[] = [];
  private navigation$ = new BehaviorSubject({
    previousScreen: undefined,
    currentscreen: undefined,
    tabId: undefined,
    tabName: undefined,
    type: undefined,
    tabs: undefined,
  });
  private serviceName = 'ScreenService';

  get currentScreen(): string {
    return this.currentscreen;
  }

  set currentScreen(value: string) {
    this.currentscreen = value;
  }

  set previousScreen(value: string) {
    this.previouscreen = value;
  }

  get previousScreen(): string {
    return this.previouscreen;
  }

  get searchlink(): string {
    return this.searchLink;
  }

  set searchlink(value: string) {
    this.searchLink = value;
  }

  constructor(
    private router: Router,
    private tabService: TabService,
    private activatedRoute: ActivatedRoute,
    private readonly subscriptionHandlerService: SubscriptionHandlerService,
    private newrelic: NewrelicService,
    private dataService: DataService
  ) {
    const navigation: Subscription = this.navigation$.asObservable().subscribe((result) => {
      if (result.type === 'tab') {
        this.tabService.currentTabId = result.tabId;
        this.tabService.currentTabName = result.tabName;
      } else {
        this.setCurrentScreen(result.currentscreen);
      }
    });
    this.subscriptionHandlerService.addSubscription(this.serviceName, 'navigation', navigation);

    const routerEvents: Subscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Saves the route if it is valid and if it is not equal to the current url
        if (this.isValidBackUrl(event.urlAfterRedirects) && event.urlAfterRedirects !== this.history?.[-1]) {
          this.history?.push(event.urlAfterRedirects);
        }
        const pastPageId = this.newrelic.store.getPageID();
        let pageId =
          this.activatedRoute?.snapshot?.firstChild?.data?.pageID || this.activatedRoute?.firstChild?.snapshot?.firstChild?.data?.pageID;
        if (this.router.url.includes('epg')) {
          pageId = PageType.Epg;
        }
        this.setCurrentScreen(pageId);
        this.newrelic.store.setScreenInfo({
          currentScreen: pageId,
          previousScreen: pastPageId,
        });
        this.newrelic.store.setPageID(pageId);
      }
    });
    this.subscriptionHandlerService.addSubscription(this.serviceName, 'routerEvents', routerEvents);
  }

  setCurrentScreen(currentScreen: string): void {
    this.previouscreen = this.currentscreen;

    if (currentScreen === PageType.Browse) {
      this.currentscreen = this.tabService.currentTabName;
    } else {
      this.currentscreen = currentScreen;
    }
  }

  navigateBack(): void {
    if (this.history.length >= 1) {
      const routeToNavigate = this.history.pop();
      // Navigates to the previous URL
      const backRoute =
        this.isValidBackUrl(routeToNavigate) && routeToNavigate !== this.router?.url ? routeToNavigate : this.getValidBackRoute();
      // The history is cleared when the user navigates to a main tab
      if (this.tabService?.getTab(backRoute?.replace('/', ''))) this.history = [];
      this.router
        .navigate([backRoute])
        .then()
        .catch(() => {
          this.router.navigate([backRoute]).then();
        });
    } else {
      this.router.navigate([this.dataService.homeTab], { state: { tabId: this.dataService.homeTab } });
    }
  }

  /**
   * Checks if a URL is valid
   * */
  private isValidBackUrl(url: string): boolean {
    return !url.includes('player') && !url.includes('link-device');
  }

  /**
   * Gets the back URL
   * */
  private getValidBackRoute() {
    while (this.history?.length > 0) {
      const checkingRoute = this.history.pop();
      if (!this.isValidBackUrl(checkingRoute) || checkingRoute === this.dataService.currentUrl) {
        continue;
      }
      return checkingRoute;
    }
    return this.dataService?.homeTab;
  }
}
